import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import { User, CreditCard, Smartphone, CheckCircle, AlertCircle, Calendar, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BORDER_CLASSES = {
  approved: 'border-[#4A6B48] bg-[#4A6B48]/5',
  payment_pending: 'border-blue-300 bg-blue-50',
  rejected: 'border-red-300 bg-red-50',
  pending: 'border-orange-300 bg-orange-50',
};

const STATUS_TITLES = {
  approved: 'Adhésion Active',
  payment_pending: 'Paiement en cours de vérification',
  pending: 'Paiement requis',
  rejected: 'Adhésion rejetée',
};

const getStatusIcon = (status) => {
  if (status === 'approved') return <CheckCircle className="text-[#4A6B48]" size={24} />;
  if (status === 'rejected') return <AlertCircle className="text-red-600" size={24} />;
  return <AlertCircle className="text-orange-600" size={24} />;
};

export default function MemberPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [membership, setMembership] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('info');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', motivation: '',
    nationality: '', country_of_residence: ''
  });
  const [paymentData, setPaymentData] = useState({
    payment_reference: '',
    payment_method: 'Mobile Money',
    payment_phone: '',
    payment_amount: 5.0
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      navigate('/login');
    }
  }, [navigate]);

  const fetchMembership = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/members/me`, { withCredentials: true });
      setMembership(response.data);
      setFormData({
        name: response.data.name,
        email: response.data.email,
        phone: response.data.phone,
        address: response.data.address,
        motivation: response.data.motivation,
        nationality: response.data.nationality || '',
        country_of_residence: response.data.country_of_residence || ''
      });
      if (response.data.status === 'pending') setStep('payment');
      else if (response.data.status === 'payment_pending') setStep('waiting');
      else if (response.data.status === 'approved') setStep('approved');
      else if (response.data.status === 'rejected') setStep('rejected');
    } catch (error) {
      setStep('info');
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSettings(response.data);
    } catch (error) { console.error(error); }
  }, []);

  useEffect(() => {
    if (location.state?.user) return;
    checkAuth();
  }, [location.state, checkAuth]);

  useEffect(() => {
    if (user) {
      fetchMembership();
      fetchSettings();
    }
  }, [user, fetchMembership, fetchSettings]);

  useEffect(() => {
    if (settings) {
      setPaymentData(prev => ({ ...prev, payment_amount: settings.membership_fee_usd || 5.0 }));
    }
  }, [settings]);

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/members/join`, formData);
      toast.success('Inscription enregistrée ! Veuillez maintenant payer les frais d\'adhésion.');
      fetchMembership();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/members/submit-payment`, paymentData, { withCredentials: true });
      toast.success('Preuve de paiement soumise ! L\'administrateur va vérifier et valider votre adhésion.');
      fetchMembership();
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated === null) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div></div>;
  }
  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster />
      <Navigation />

      <section className="section-padding bg-white" data-testid="member-page">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="member-title">
                Espace Membre
              </h1>
              <p className="text-[#5C665A]">Bienvenue, {user?.name} !</p>
            </div>

            {/* Status Banner */}
            {membership && (
              <div className={`card mb-6 ${STATUS_BORDER_CLASSES[membership.status] || STATUS_BORDER_CLASSES.pending}`}>
                <div className="flex items-start gap-3">
                  {getStatusIcon(membership.status)}
                  <div className="flex-1">
                    <h3 className="font-bold text-[#2E332D]">
                      {STATUS_TITLES[membership.status]}
                    </h3>
                    <p className="text-sm text-[#5C665A] mt-1">
                      {membership.status === 'approved' && membership.membership_expires_at &&
                        `Votre adhésion est valide jusqu'au ${new Date(membership.membership_expires_at).toLocaleDateString('fr-FR', { dateStyle: 'long' })}`}
                      {membership.status === 'payment_pending' && 'Nous avons reçu votre preuve de paiement. L\'administrateur vérifie le paiement et validera votre adhésion sous peu.'}
                      {membership.status === 'pending' && 'Payez les frais d\'adhésion via Mobile Money pour activer votre adhésion.'}
                      {membership.status === 'rejected' && 'Veuillez contacter l\'administrateur pour plus d\'informations.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 1: Registration Form */}
            {step === 'info' && (
              <div className="card">
                <h2 className="text-2xl font-bold text-[#2E332D] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Étape 1 : Demande d&apos;adhésion
                </h2>
                <p className="text-sm text-[#5C665A] mb-6">
                  Complétez ce formulaire pour soumettre votre demande d&apos;adhésion à CRESA ASBL.
                </p>

                <form onSubmit={handleJoinSubmit} data-testid="membership-form">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">Nom complet *</label>
                      <input type="text" name="name" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="member-name-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">Email *</label>
                      <input type="email" name="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="member-email-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">Téléphone *</label>
                      <input type="tel" name="phone" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="member-phone-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">Adresse *</label>
                      <input type="text" name="address" required value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="member-address-input" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#2E332D] mb-2">Nationalité</label>
                        <input
                          type="text" name="nationality"
                          value={formData.nationality}
                          onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                          placeholder="Ex: Congolaise"
                          className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                          data-testid="member-nationality-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2E332D] mb-2">Pays de résidence</label>
                        <input
                          type="text" name="country_of_residence"
                          value={formData.country_of_residence}
                          onChange={(e) => setFormData({...formData, country_of_residence: e.target.value})}
                          placeholder="Ex: République Démocratique du Congo"
                          className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                          data-testid="member-country-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E332D] mb-2">Motivation pour rejoindre CRESA *</label>
                      <textarea name="motivation" required rows={4} value={formData.motivation} onChange={(e) => setFormData({...formData, motivation: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="member-motivation-input" />
                    </div>
                    <button type="submit" disabled={loading} className="w-full btn-primary py-3 disabled:opacity-50" data-testid="submit-membership-button">
                      {loading ? 'Envoi...' : 'Soumettre ma demande'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* STEP 2: Payment Instructions */}
            {step === 'payment' && (
              <div className="card" data-testid="payment-section">
                <h2 className="text-2xl font-bold text-[#2E332D] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Étape 2 : Paiement des frais d&apos;adhésion
                </h2>

                {/* Payment Instructions */}
                <div className="bg-[#4A6B48]/5 border-2 border-[#4A6B48] rounded-lg p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Smartphone className="text-[#4A6B48]" size={28} />
                    <h3 className="text-xl font-bold text-[#2E332D]">Instructions de paiement Mobile Money</h3>
                  </div>

                  {settings?.mobile_money_number ? (
                    <>
                      <div className="space-y-3 text-[#2E332D]">
                        <div className="flex justify-between items-center pb-3 border-b border-[#E5E3DB]">
                          <span className="font-medium">Montant à payer :</span>
                          <span className="text-2xl font-bold text-[#D87D4A]">
                            ${settings.membership_fee_usd} USD / an
                          </span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-[#E5E3DB]">
                          <span className="font-medium">Opérateur :</span>
                          <span className="font-bold">{settings.mobile_money_operator || 'Mobile Money'}</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-[#E5E3DB]">
                          <span className="font-medium">Numéro à payer :</span>
                          <span className="text-xl font-bold text-[#4A6B48]" data-testid="mm-number-display">
                            {settings.mobile_money_number}
                          </span>
                        </div>
                      </div>

                      <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-sm text-[#2E332D]">
                          <strong>Procédure :</strong>
                        </p>
                        <ol className="text-sm text-[#5C665A] mt-2 space-y-1 list-decimal list-inside">
                          <li>Effectuez un transfert de <strong>${settings.membership_fee_usd} USD</strong> via {settings.mobile_money_operator || 'Mobile Money'} au numéro <strong>{settings.mobile_money_number}</strong></li>
                          <li>Conservez le <strong>numéro de référence de la transaction</strong> reçu par SMS</li>
                          <li>Remplissez le formulaire ci-dessous avec les détails de votre paiement</li>
                          <li>L&apos;administrateur vérifiera votre paiement et activera votre adhésion sous 24-48h</li>
                        </ol>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-[#5C665A]">
                      Les informations de paiement Mobile Money n&apos;ont pas encore été configurées par l&apos;administrateur.
                      Veuillez nous contacter pour plus d&apos;informations.
                    </p>
                  )}
                </div>

                {/* Payment Proof Form */}
                {settings?.mobile_money_number && (
                  <form onSubmit={handlePaymentSubmit} data-testid="payment-proof-form">
                    <h3 className="text-lg font-bold text-[#2E332D] mb-4 flex items-center gap-2">
                      <CreditCard size={20} className="text-[#4A6B48]" />
                      Soumettre la preuve de paiement
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#2E332D] mb-2">
                          Numéro de référence de la transaction *
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="Ex: MP240527.1234.A12345"
                          value={paymentData.payment_reference}
                          onChange={(e) => setPaymentData({...paymentData, payment_reference: e.target.value})}
                          className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                          data-testid="payment-reference-input"
                        />
                        <p className="text-xs text-[#5C665A] mt-1">
                          Le numéro de référence se trouve dans le SMS de confirmation
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2E332D] mb-2">
                          Numéro Mobile Money payeur *
                        </label>
                        <input
                          type="tel"
                          required
                          placeholder="+243 XXX XXX XXX"
                          value={paymentData.payment_phone}
                          onChange={(e) => setPaymentData({...paymentData, payment_phone: e.target.value})}
                          className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                          data-testid="payment-phone-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2E332D] mb-2">
                          Méthode de paiement *
                        </label>
                        <select
                          value={paymentData.payment_method}
                          onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
                          className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                        >
                          <option value="M-Pesa">M-Pesa</option>
                          <option value="Airtel Money">Airtel Money</option>
                          <option value="Orange Money">Orange Money</option>
                          <option value="Africell Money">Africell Money</option>
                          <option value="Mobile Money">Autre Mobile Money</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2E332D] mb-2">
                          Montant payé (USD) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={paymentData.payment_amount}
                          onChange={(e) => setPaymentData({...paymentData, payment_amount: parseFloat(e.target.value)})}
                          className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                          data-testid="payment-amount-input"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full btn-primary py-3 disabled:opacity-50 inline-flex items-center justify-center"
                        data-testid="submit-payment-proof-button"
                      >
                        <Send size={18} className="mr-2" />
                        {loading ? 'Envoi en cours...' : 'Soumettre la preuve de paiement'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* STEP 3: Waiting for validation */}
            {step === 'waiting' && (
              <div className="card text-center py-8" data-testid="waiting-section">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                  <Calendar className="text-blue-600" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Paiement en cours de vérification
                </h2>
                <p className="text-[#5C665A] mb-4">
                  Merci ! Nous avons bien reçu votre preuve de paiement.<br />
                  L&apos;administrateur va vérifier le paiement et valider votre adhésion.
                </p>
                {membership?.payment_reference && (
                  <div className="inline-block bg-[#F9F8F6] p-4 rounded-lg text-left mt-4">
                    <p className="text-sm"><strong>Référence:</strong> {membership.payment_reference}</p>
                    <p className="text-sm"><strong>Méthode:</strong> {membership.payment_method}</p>
                    <p className="text-sm"><strong>Montant:</strong> ${membership.payment_amount} USD</p>
                  </div>
                )}
              </div>
            )}

            {/* STEP 4: Approved */}
            {step === 'approved' && (
              <div className="card text-center py-8" data-testid="approved-section">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#4A6B48]/10 rounded-full flex items-center justify-center">
                  <CheckCircle className="text-[#4A6B48]" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Adhésion Active !
                </h2>
                <p className="text-[#5C665A] mb-4">
                  Félicitations ! Vous êtes membre actif de CRESA ASBL.
                </p>
                {membership?.admin_decision_message && (
                  <div className="text-left bg-[#4A6B48]/5 border border-[#4A6B48]/30 p-4 rounded-lg mb-4">
                    <p className="text-xs font-bold text-[#4A6B48] mb-2">Message de l&apos;administration :</p>
                    <p className="text-sm text-[#2E332D] whitespace-pre-wrap">{membership.admin_decision_message}</p>
                  </div>
                )}
                {membership?.membership_expires_at && (
                  <div className="inline-block bg-[#4A6B48]/5 p-4 rounded-lg">
                    <p className="text-sm text-[#2E332D]">
                      <Calendar className="inline mr-2" size={16} />
                      Votre adhésion expire le <strong>{new Date(membership.membership_expires_at).toLocaleDateString('fr-FR', { dateStyle: 'long' })}</strong>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* STEP 5: Rejected */}
            {step === 'rejected' && (
              <div className="card text-center py-8" data-testid="rejected-section">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="text-red-600" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Adhésion non validée
                </h2>
                {membership?.admin_decision_message ? (
                  <div className="text-left bg-red-50 border border-red-200 p-4 rounded-lg max-w-xl mx-auto">
                    <p className="text-xs font-bold text-red-700 mb-2">Message de l&apos;administration :</p>
                    <p className="text-sm text-[#2E332D] whitespace-pre-wrap">{membership.admin_decision_message}</p>
                  </div>
                ) : (
                  <p className="text-[#5C665A]">
                    Veuillez contacter l&apos;administrateur pour plus d&apos;informations.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
